import { sortBy, uniq, uniqBy } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useApplicationConfig } from '~/data/applicationConfig';
import { useModulesConfig } from '~/data/company';
import { useUILabelTranslation } from '~/data/enums';
import { Modules } from '~/data/modules';
import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';
import { useModuleAccess } from '~/hooks/useModuleAccess';
import { PeopleModuleBasicDataAccessMode } from '~/scenes/PeopleModule/components/Tabs/BasicTab';
import { prefetchCustomData } from '~/scenes/PeopleModule/components/Tabs/CustomDataTab/data';
import { prefetchCustomPosition } from '~/scenes/PeopleModule/components/Tabs/CustomPositionTab/data';
import { useEnabledModuleTabPaths } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/components/EmployeeModuleTabs/useEnabledModuleTabPaths';
import { usePrepModuleTabProps } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/components/EmployeeModuleTabs/usePrepModuleTabProps';
import {
  EmployeeModuleRouting_People,
  EmployeeModuleTabPath_People,
} from '~/scenes/PortalViews/Views/EmployeeViews/Modules/PeopleModule/data';
import {
  EmployeeModuleTabAccessMode,
  EmployeeModuleTabProps,
  EmployeeModuleTabSetupProps,
} from '~/scenes/PortalViews/Views/EmployeeViews/Modules/types';
import { isRoleITAdmin } from '~/services/Permissions';

import { useLimitedAccessPeopleTabs } from '../components/EmployeeModuleTabs/useLimitedAccessPeopleTabs';

type PropsType = {
  hasFullAccess: boolean;
  modulePath: string;
};

type UseEmployeePeopleModuleOutput<TabPath> = {
  isLoading: boolean;
  employeeId: number;
  enabledTabPaths: TabPath[];
  defaultTab: TabPath;
  modulePath: string;
  tabs: EmployeeModuleTabProps<TabPath>[];
  accessMode: PeopleModuleBasicDataAccessMode;
};

export const useEmployeePeopleModule = (
  props: PropsType,
): UseEmployeePeopleModuleOutput<EmployeeModuleTabPath_People> => {
  const { hasFullAccess, modulePath } = props;

  const myRoles = useLoggedInUserRoles();

  const { t } = useTranslation();
  const { uiLabelTranslation } = useUILabelTranslation();

  const { selectedEmployeeId, isSelectedEmployeeLoggedInUser } =
    useSelectedEmployeeId();
  const moduleAccess = useModuleAccess();
  const { isModuleEnabled } = useModulesConfig();

  const { limitedAccessTabs, tabsPropsLimitedAccessTabs } =
    useLimitedAccessPeopleTabs();

  // Resolve Enabled Tabs
  const { applicationConfig, hasData } = useApplicationConfig();

  const accessMode: EmployeeModuleTabAccessMode = useMemo(() => {
    if (hasFullAccess) {
      return EmployeeModuleTabAccessMode.Full;
    } else if (isRoleITAdmin(myRoles)) {
      return EmployeeModuleTabAccessMode.ITAdmin;
    } else {
      return EmployeeModuleTabAccessMode.Limited;
    }
  }, [hasFullAccess, myRoles]);

  const { availableTabs } = useMemo(() => {
    const allTabs = Object.values(EmployeeModuleTabPath_People);

    const getUserAccessibleTabs = () => {
      switch (accessMode) {
        case EmployeeModuleTabAccessMode.Full: {
          if (isSelectedEmployeeLoggedInUser) {
            return allTabs;
          }

          const restrictTabConfig: {
            [key in EmployeeModuleTabPath_People]?: boolean;
          } = {
            [EmployeeModuleTabPath_People.Performance]:
              !moduleAccess.performance,
            [EmployeeModuleTabPath_People.Development]:
              !moduleAccess.development,
          };

          const restrictTabs = Object.entries(restrictTabConfig).reduce(
            (tabs, [tab, restrict]) => (restrict ? [...tabs, tab] : tabs),
            [] as string[],
          );

          return restrictTabs.length > 0
            ? allTabs.filter((tab) => !restrictTabs.includes(tab))
            : allTabs;
        }
        case EmployeeModuleTabAccessMode.ITAdmin:
          return uniq([
            ...limitedAccessTabs,
            EmployeeModuleTabPath_People.Basic,
            EmployeeModuleTabPath_People.CustomData,
            EmployeeModuleTabPath_People.Contact,
            EmployeeModuleTabPath_People.Equipment,
            EmployeeModuleTabPath_People.Absences,
            EmployeeModuleTabPath_People.Collaboration,
          ]);
        case EmployeeModuleTabAccessMode.Limited:
          return limitedAccessTabs;
      }
    };

    const userAccessibleTabs = getUserAccessibleTabs();

    const tabConditions: { [key in EmployeeModuleTabPath_People]?: boolean } = {
      [EmployeeModuleTabPath_People.DecisionRegistry]:
        hasData &&
        applicationConfig?.fComConfig?.miscellaneousConfig
          ?.fComEnableDecisionRegistry,
      [EmployeeModuleTabPath_People.Collaboration]: isModuleEnabled(
        Modules.COLLABORATION,
      ),
    };

    const availableTabs = allTabs
      .filter((path) => userAccessibleTabs?.includes(path))
      .filter((path) => {
        return Object.keys(tabConditions).includes(path)
          ? tabConditions[path]
          : true;
      });

    return { userAccessibleTabs, tabConditions, availableTabs };
  }, [
    accessMode,
    hasData,
    applicationConfig,
    isSelectedEmployeeLoggedInUser,
    moduleAccess,
    limitedAccessTabs,
    isModuleEnabled,
  ]);

  // Tabs data
  const { tabProps } = useMemo(() => {
    const tabsProps_Full: EmployeeModuleTabSetupProps<EmployeeModuleTabPath_People>[] =
      [
        {
          id: EmployeeModuleTabPath_People.Basic,
          title: t('laBasicData'),
        },
        {
          id: EmployeeModuleTabPath_People.CustomData,
          title: t('laCustomData'),
          prefetchData: () => prefetchCustomData(selectedEmployeeId.toString()),
        },
        {
          id: EmployeeModuleTabPath_People.JobDescription,
          title: t('laPositionCustomData'),
          prefetchData: () =>
            prefetchCustomPosition(selectedEmployeeId.toString()),
        },
        {
          id: EmployeeModuleTabPath_People.Contact,
          title: t('laContact'),
        },
        {
          id: EmployeeModuleTabPath_People.Equipment,
          title: t('laComEq'),
        },
        {
          id: EmployeeModuleTabPath_People.Documents,
          title: t('laEmpDocs'),
        },
        {
          id: EmployeeModuleTabPath_People.Absences,
          title: t('laTimeOff'),
        },
        {
          id: EmployeeModuleTabPath_People.DecisionRegistry,
          title: t('laDecisionRegistry'),
        },
        {
          id: EmployeeModuleTabPath_People.Collaboration,
          title: uiLabelTranslation('laChecklistsAndTemplates'),
        },
        {
          id: EmployeeModuleTabPath_People.Performance,
          title: t('laPerformance'),
          variant: 'summary',
        },
        {
          id: EmployeeModuleTabPath_People.Development,
          title: t('laDevelopment'),
          variant: 'summary',
        },
      ];

    const tabsOrder = tabsProps_Full.map((t) => t.id);
    const sortTabs = (
      tabs: EmployeeModuleTabSetupProps<EmployeeModuleTabPath_People>[],
    ) => sortBy(tabs, (tab) => tabsOrder.indexOf(tab.id));

    const tabsProps_ITAdmin: EmployeeModuleTabSetupProps<EmployeeModuleTabPath_People>[] =
      sortTabs(
        uniqBy(
          [
            ...tabsPropsLimitedAccessTabs,
            {
              id: EmployeeModuleTabPath_People.Basic,
              title: t('laBasicData'),
            },
            {
              id: EmployeeModuleTabPath_People.CustomData,
              title: t('laCustomData'),
              prefetchData: () =>
                prefetchCustomData(selectedEmployeeId.toString()),
            },
            {
              id: EmployeeModuleTabPath_People.Contact,
              title: t('laContact'),
            },
            {
              id: EmployeeModuleTabPath_People.Equipment,
              title: t('laComEq'),
            },
            {
              id: EmployeeModuleTabPath_People.Absences,
              title: t('laVacations'),
            },
            {
              id: EmployeeModuleTabPath_People.Collaboration,
              title: uiLabelTranslation('laChecklistsAndTemplates'),
            },
          ],
          'id',
        ),
      );

    const tabsProps_Limited = sortTabs(tabsPropsLimitedAccessTabs);

    const getTabProps =
      (): EmployeeModuleTabSetupProps<EmployeeModuleTabPath_People>[] => {
        const mapToAccessMode =
          (accessMode: EmployeeModuleTabAccessMode) =>
          (
            p: EmployeeModuleTabSetupProps<EmployeeModuleTabPath_People>,
          ): EmployeeModuleTabSetupProps<EmployeeModuleTabPath_People> => ({
            ...p,
            accessMode,
          });

        switch (accessMode) {
          case EmployeeModuleTabAccessMode.Full:
            return tabsProps_Full.map(
              mapToAccessMode(EmployeeModuleTabAccessMode.Full),
            );
          case EmployeeModuleTabAccessMode.ITAdmin:
            return tabsProps_ITAdmin.map(
              mapToAccessMode(EmployeeModuleTabAccessMode.ITAdmin),
            );
          default:
            return tabsProps_Limited.map(
              mapToAccessMode(EmployeeModuleTabAccessMode.Limited),
            );
        }
      };

    return {
      tabProps: getTabProps(),
    };
  }, [
    accessMode,
    selectedEmployeeId,
    t,
    uiLabelTranslation,
    tabsPropsLimitedAccessTabs,
  ]);

  const { enabledTabConfigs, enabledTabPaths, defaultTab } =
    useEnabledModuleTabPaths({
      ...EmployeeModuleRouting_People,
      availableTabs,
    });

  // Resolve Tab props
  const { tabs, isLoading: isLoadingTabPrep } = usePrepModuleTabProps({
    ...EmployeeModuleRouting_People,
    tabConfigs: enabledTabConfigs,
    modulePath,
    tabsSetupProps: tabProps,
    enabledTabPaths,
  });

  const peopleModuleAccessMode: PeopleModuleBasicDataAccessMode =
    useMemo(() => {
      switch (accessMode) {
        case EmployeeModuleTabAccessMode.Full:
          return PeopleModuleBasicDataAccessMode.Full;
        case EmployeeModuleTabAccessMode.ITAdmin:
          return PeopleModuleBasicDataAccessMode.ITAdmin;
        case EmployeeModuleTabAccessMode.Limited:
        default:
          return PeopleModuleBasicDataAccessMode.Limited;
      }
    }, [accessMode]);

  return {
    isLoading: isLoadingTabPrep,
    tabs,
    defaultTab,
    employeeId: selectedEmployeeId,
    enabledTabPaths,
    modulePath,
    accessMode: peopleModuleAccessMode,
  };
};
